import { useUserStore } from '~/stores/user'
import {
  decideOrganizationSlugAction,
  isUuidShaped,
} from '~/utils/organizationSlugCanonical'

/**
 * Canonicalise organization-slug URLs.
 *
 * - Legacy `/wsid/…` (UUID in the organizationSlug slot) → redirect to `/:slug/:wsid/…`
 * - Stale `/wrong-slug/:wsid/…` → redirect to the correct slug
 * - Anything else → fall through (the access gate owns membership → 404)
 *
 * Each case is decided in `decideOrganizationSlugAction` (~/utils/organizationSlugCanonical.ts),
 * where the same labels mark the matching code blocks — this middleware only
 * executes the verdict.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const organizationSlugParam = to.params.organizationSlug
  const workspaceIdParam = to.params.workspaceId

  // A deep legacy `/wsid/…` link matches no route, so both params come back
  // empty. Fall back to the raw first segment: UUID-shaped means the URL
  // starts with a workspace id and still needs redirecting.
  // e.g. `/1111-…/tests/xyz` → first segment `1111-…`
  const startsWithWorkspaceId = isUuidShaped(
    to.path.split('/').filter(Boolean)[0],
  )

  // Skip anything that's neither an organization route, a workspace route,
  // nor a legacy link — avoids loading the user store on every navigation.
  // e.g. `/`, `/signin`, `/invite`, `/system-admin`, `/random/stuff`
  if (!organizationSlugParam && !workspaceIdParam && !startsWithWorkspaceId)
    return

  // Past this point the URL is one of: Legacy, Stale, Unknown organization-level
  // slug, Non-member, or already canonical.

  const userStore = useUserStore()

  try {
    await userStore.$ensureInitialised()
  } catch {
    // Can't load membership → fail open: skip canonicalisation and let
    // the existing auth/404 handling decide. Better a stale URL than a
    // blocked navigation.
    return
  }

  const decision = decideOrganizationSlugAction({
    path: to.path,
    organizationSlug:
      typeof organizationSlugParam === 'string'
        ? organizationSlugParam
        : undefined,
    workspaceId:
      typeof workspaceIdParam === 'string' ? workspaceIdParam : undefined,
    query: to.query,
    getOrganizationSlugForWorkspace: (workspaceId) =>
      userStore.getOrganizationSlugForWorkspace(workspaceId),
  })

  // Legacy `/wsid/…` or Stale `/wrong-slug/:wsid/…` → redirect to canonical.
  // e.g. `/1111-…/tests` or `/old-name/1111-…/tests` → `/acme/1111-…/tests`
  if (decision.type === 'redirect') {
    // Drop returnTo so canonicalisation can never feed the signin
    // redirect chain with a user-controlled target.
    const query = { ...decision.query }
    delete query.returnTo
    return navigateTo({ path: decision.path, query })
  }

  // Unknown/non-member organization, non-member workspace, or already canonical → fall
  // through (continue). The access gate (05.access) owns the membership → 404
  // verdict. e.g. `/acme/1111-…/tests` with the correct slug.
})
